import React, { useCallback, useEffect, useReducer, useRef } from "react";
import { SYM } from "../../utils/textSymbols";
import {
  PIECE_COLORS,
  PIECE_NAMES,
  SHAPES,
  buildDisplayGrid,
  clearLines,
  collides,
  createPiece,
  dropIntervalMs,
  initialGameState,
  mergePiece,
  previewGrid,
  saveHighScore,
  scoreForLines
} from "./coolBlockEngine";
import "./CoolBlockGamePage.css";

function gameReducer(state, action) {
  switch (action.type) {
    case "START":
      return { ...initialGameState(), status: "playing", highScore: state.highScore };
    case "PAUSE":
      return { ...state, status: state.status === "playing" ? "paused" : "playing" };
    case "TICK": {
      if (state.status !== "playing") return state;
      const moved = { ...state.current, y: state.current.y + 1 };
      if (!collides(state.board, moved)) {
        return { ...state, current: moved };
      }
      const board = mergePiece(state.board, state.current);
      const { board: clearedBoard, cleared } = clearLines(board);
      const lines = state.lines + cleared;
      const level = Math.floor(lines / 10) + 1;
      const score = state.score + scoreForLines(cleared, state.level);
      const current = state.next;
      const next = createPiece();
      if (collides(clearedBoard, current)) {
        const highScore = Math.max(state.highScore, score);
        saveHighScore(highScore);
        return {
          ...state,
          board: clearedBoard,
          current,
          next,
          score,
          lines,
          level,
          status: "over",
          highScore
        };
      }
      return {
        ...state,
        board: clearedBoard,
        current,
        next,
        score,
        lines,
        level
      };
    }
    case "MOVE": {
      if (state.status !== "playing") return state;
      const moved = { ...state.current, x: state.current.x + action.dx };
      if (collides(state.board, moved)) return state;
      return { ...state, current: moved };
    }
    case "ROTATE": {
      if (state.status !== "playing") return state;
      const rotations = SHAPES[state.current.kind].length;
      const nextRot = (state.current.rotation + 1) % rotations;
      const kicks = [0, -1, 1, -2, 2];
      for (const kick of kicks) {
        const rotated = { ...state.current, rotation: nextRot, x: state.current.x + kick };
        if (!collides(state.board, rotated)) {
          return { ...state, current: rotated };
        }
      }
      return state;
    }
    case "SOFT_DROP": {
      if (state.status !== "playing") return state;
      const moved = { ...state.current, y: state.current.y + 1 };
      if (!collides(state.board, moved)) {
        return { ...state, current: moved, score: state.score + 1 };
      }
      return gameReducer(state, { type: "TICK" });
    }
    case "HARD_DROP": {
      if (state.status !== "playing") return state;
      let piece = { ...state.current };
      let bonus = 0;
      while (!collides(state.board, { ...piece, y: piece.y + 1 })) {
        piece = { ...piece, y: piece.y + 1 };
        bonus += 2;
      }
      const withPiece = { ...state, current: piece, score: state.score + bonus };
      return gameReducer(withPiece, { type: "TICK" });
    }
    default:
      return state;
  }
}

function Cell({ kind, active }) {
  const color = kind ? PIECE_COLORS[kind] : undefined;
  return (
    <div
      className={`cbg-cell ${kind ? "filled" : ""} ${active ? "active" : ""}`}
      style={color ? { backgroundColor: color } : undefined}
    />
  );
}

function NextPreview({ kind }) {
  const grid = previewGrid(kind);
  return (
    <div className="cbg-next-grid" aria-hidden="true">
      {grid.flat().map((cell, i) => (
        <div
          key={i}
          className={`cbg-next-cell ${cell ? "filled" : ""}`}
          style={cell ? { backgroundColor: PIECE_COLORS[cell] } : undefined}
        />
      ))}
    </div>
  );
}

export default function CoolBlockGamePage({ onBack }) {
  const [state, dispatch] = useReducer(gameReducer, null, initialGameState);
  const stateRef = useRef(state);
  stateRef.current = state;

  const rotate = useCallback(() => {
    if (stateRef.current.status !== "playing") return;
    dispatch({ type: "ROTATE" });
  }, []);

  const move = useCallback((dx) => {
    dispatch({ type: "MOVE", dx });
  }, []);

  const softDrop = useCallback(() => {
    dispatch({ type: "SOFT_DROP" });
  }, []);

  const hardDrop = useCallback(() => {
    dispatch({ type: "HARD_DROP" });
  }, []);

  useEffect(() => {
    if (state.status !== "playing") return undefined;
    const id = setInterval(() => dispatch({ type: "TICK" }), dropIntervalMs(state.level));
    return () => clearInterval(id);
  }, [state.status, state.level]);

  useEffect(() => {
    const onKeyDown = (e) => {
      const { status } = stateRef.current;
      if (status === "ready" || status === "over") {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          dispatch({ type: "START" });
        }
        return;
      }
      if (status === "paused") {
        if (e.key === "p" || e.key === "P" || e.key === "Escape") {
          dispatch({ type: "PAUSE" });
        }
        return;
      }
      if (status !== "playing") return;

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          move(-1);
          break;
        case "ArrowRight":
          e.preventDefault();
          move(1);
          break;
        case "ArrowDown":
          e.preventDefault();
          softDrop();
          break;
        case "ArrowUp":
        case "x":
        case "X":
          e.preventDefault();
          rotate();
          break;
        case " ":
          e.preventDefault();
          hardDrop();
          break;
        case "p":
        case "P":
        case "Escape":
          e.preventDefault();
          dispatch({ type: "PAUSE" });
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [move, rotate, softDrop, hardDrop]);

  const displayGrid = buildDisplayGrid(state.board, state.status === "playing" ? state.current : null);
  const showOverlay = state.status === "ready" || state.status === "paused" || state.status === "over";
  const nextName = PIECE_NAMES[state.next?.kind] || "";

  return (
    <div className="cbg-page">
      <header className="cbg-header">
        <button type="button" className="cbg-back-btn" onClick={onBack} aria-label="Back to games">
          {SYM.arrowLeft} Games
        </button>
        <div className="cbg-title-wrap">
          <h1 className="cbg-title">Cool Block Game</h1>
          <p className="cbg-tagline">Definitely not a falling-block puzzle</p>
        </div>
        {(state.status === "playing" || state.status === "paused") && (
          <button type="button" className="cbg-pause-btn" onClick={() => dispatch({ type: "PAUSE" })}>
            {state.status === "paused" ? "Play" : "Pause"}
          </button>
        )}
      </header>

      <div className="cbg-main">
        <aside className="cbg-side" aria-label="Stats">
          <div className="cbg-stat-card">
            <div className="cbg-stat-label">Score</div>
            <div className="cbg-stat-value">{state.score}</div>
          </div>
          <div className="cbg-stat-card">
            <div className="cbg-stat-label">Best</div>
            <div className="cbg-stat-value">{state.highScore}</div>
          </div>
          <div className="cbg-stat-card">
            <div className="cbg-stat-label">Level</div>
            <div className="cbg-stat-value">{state.level}</div>
          </div>
          <div className="cbg-stat-card">
            <div className="cbg-stat-label">Next block</div>
            <NextPreview kind={state.next?.kind} />
            <div className="cbg-stat-label" style={{ marginTop: 6 }}>{nextName}</div>
          </div>
        </aside>

        <div className="cbg-board-wrap">
          <div className="cbg-board-outer">
            <div className="cbg-board" role="grid" aria-label="Playfield">
              {displayGrid.flat().map((cell, i) => (
                <Cell
                  key={i}
                  kind={cell?.kind}
                  active={cell && !cell.locked}
                />
              ))}
            </div>
            {showOverlay && (
              <div className="cbg-overlay">
                {state.status === "ready" && (
                  <>
                    <h2 className="cbg-overlay-title">Stack cool blocks</h2>
                    <p className="cbg-overlay-sub">
                      Clear full rows. The shapes are 100% original and legally distinct.
                    </p>
                    <button type="button" className="cbg-primary-btn" onClick={() => dispatch({ type: "START" })}>
                      Play
                    </button>
                  </>
                )}
                {state.status === "paused" && (
                  <>
                    <h2 className="cbg-overlay-title">Paused</h2>
                    <button type="button" className="cbg-primary-btn" onClick={() => dispatch({ type: "PAUSE" })}>
                      Resume
                    </button>
                  </>
                )}
                {state.status === "over" && (
                  <>
                    <h2 className="cbg-overlay-title">Nice stacking!</h2>
                    <p className="cbg-overlay-sub">Score: {state.score}</p>
                    <button type="button" className="cbg-primary-btn" onClick={() => dispatch({ type: "START" })}>
                      Play again
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="cbg-controls" aria-label="Touch controls">
            <button type="button" className="cbg-ctrl-btn" onClick={() => move(-1)} aria-label="Move left">
              {SYM.arrowLeft}
            </button>
            <button type="button" className="cbg-ctrl-btn" onClick={rotate} aria-label="Rotate">
              ↻
            </button>
            <button type="button" className="cbg-ctrl-btn" onClick={() => move(1)} aria-label="Move right">
              {SYM.arrowRight}
            </button>
            <button type="button" className="cbg-ctrl-btn" onClick={softDrop} aria-label="Soft drop">
              ↓
            </button>
            <button type="button" className="cbg-ctrl-btn wide" onClick={hardDrop}>
              Slam down (not a hard drop)
            </button>
          </div>
        </div>
      </div>

      <p className="cbg-help cbg-help-keyboard">
        Arrows move · Up rotates · Space slams · P pauses
      </p>
      <p className="cbg-help">
        Use the buttons below the board on phone or Tesla screen
      </p>
    </div>
  );
}
